import { Link } from 'react-router-dom';
import { ArrowRight, Truck, CheckCircle, Shield, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products, keyStrengths, productCategories } from '../mockData';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';

const Home = () => {
  const { addToCart } = useCart();
  const { t, getProductName, getCategoryName, language, isRTL } = useLanguage();

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://i.pinimg.com/736x/2c/bc/64/2cbc647429fb67851f22785c5c2d1b97.jpg"
            alt="EliteMeat Farm"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-${isRTL ? 'l' : 'r'} from-black/70 via-black/50 to-transparent`}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('heroTitle')}<br />
              <span className="text-red-500">{t('heroSubtitle')}</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              {t('heroDescription')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                <Link to="/products">
                  {t('viewProducts')}
                  <ArrowRight className={`${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} size={20} />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20">
                <Link to="/contact">{t('contactUs')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('shopByCategory')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('exploreRange')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {productCategories.map((category) => (
              <Link 
                key={category.id} 
                to={`/products?category=${encodeURIComponent(category.name)}`}
                className="group"
                data-testid={`category-card-${category.id}`}
              >
                <Card className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={category.image}
                      alt={getCategoryName(category.name)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="text-lg font-bold mb-1">{getCategoryName(category.name)}</h3>
                      <p className="text-sm text-gray-200">{category.description}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/products">{t('viewAllProducts')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'md:order-2' : ''}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'مرحباً بك في ' : 'Welcome to '}<span className="text-red-600">EliteMeat Farm</span>
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                {language === 'ar' 
                  ? 'إيليت ميت فارم هي مزود رائد لمنتجات اللحوم عالية الجودة، ملتزمة بتقديم التميز من المزرعة إلى مائدتك. مع سنوات من الخبرة في تربية الماشية ومعالجة اللحوم، نحافظ على أعلى معايير الجودة والسلامة والنضارة.'
                  : 'EliteMeat Farm is a leading provider of premium quality meat products, dedicated to delivering excellence from farm to table. With years of experience in livestock farming and meat processing, we maintain the highest standards of quality, safety, and freshness.'}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {language === 'ar'
                  ? 'التزامنا بممارسات الزراعة الأخلاقية ومراقبة الجودة الصارمة ورضا العملاء جعلنا اسمًا موثوقًا في الصناعة الزراعية.'
                  : 'Our commitment to ethical farming practices, rigorous quality control, and customer satisfaction has made us a trusted name in the agricultural industry.'}
              </p>
              <Button asChild className="bg-red-600 hover:bg-red-700 text-white">
                <Link to="/about">{language === 'ar' ? 'اعرف المزيد عنا' : 'Learn More About Us'}</Link>
              </Button>
            </div>
            <div className={`relative ${isRTL ? 'md:order-1' : ''}`}>
              <img
                src="https://images.unsplash.com/photo-1560493676-04071c5f467b?w=800"
                alt="Our Farm"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Strengths */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('whyChooseUs')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('whyChooseDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Leaf, title: t('farmFresh'), desc: t('farmFreshDesc') },
              { icon: CheckCircle, title: t('premiumQuality'), desc: t('premiumQualityDesc') },
              { icon: Truck, title: t('fastDelivery'), desc: t('fastDeliveryDesc') },
              { icon: Shield, title: t('halal'), desc: t('halalDesc') }
            ].map((strength, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <strength.icon className="text-red-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{strength.title}</h3>
                  <p className="text-gray-600">{strength.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'ar' ? 'منتجات مميزة' : 'Featured Products'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'ar' ? 'اكتشف مجموعتنا من منتجات اللحوم الفاخرة' : 'Discover our selection of premium quality meat products'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.slice(0, 3).map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-0 shadow-lg">
                <Link to={`/product/${product.id}`}>
                  <div className="relative h-72 overflow-hidden group">
                    <img
                      src={product.image}
                      alt={getProductName(product.name)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'}`}>
                      <span className="bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                        {getCategoryName(product.category)}
                      </span>
                    </div>
                  </div>
                </Link>
                <CardContent className="p-6">
                  <Link to={`/product/${product.id}`}>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-red-600 transition-colors">
                      {getProductName(product.name)}
                    </h3>
                  </Link>
                  <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white h-12"
                      onClick={() => addToCart(product)}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {t('addToCart')}
                    </Button>
                    <Button 
                      asChild
                      variant="outline" 
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      <Link to={`/product/${product.id}`}>{t('viewDetails')}</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-red-600 hover:bg-red-700 text-white">
              <Link to="/products">{t('viewAllProducts')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            {language === 'ar' ? 'هل أنت مستعد لتجربة الجودة الفائقة؟' : 'Ready to Experience Premium Quality?'}
          </h2>
          <p className="text-xl mb-8 opacity-90">
            {language === 'ar' 
              ? 'اطلب الآن للحصول على توصيل منتجات اللحوم الطازجة والفاخرة مباشرة إلى باب منزلك.'
              : 'Order now for delivery of fresh, premium quality meat products directly to your doorstep.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-red-600 hover:bg-gray-100">
              <Link to="/products">{language === 'ar' ? 'اطلب الآن' : 'Place Order'}</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/contact">{t('contactUs')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
